/**
 * FileUpload Component
 *
 * A reusable file upload component that supports:
 * - Direct file upload with drag-and-drop
 * - Choosing files from document library
 * - Image compression for uploaded images
 * - Multiple or single file selection
 * - File preview and removal
 * - Customizable file type restrictions
 *
 * @example
 * ```jsx
 * // Basic usage
 * <FileUpload
 *   files={files}
 *   onChange={setFiles}
 *   acceptTypes="image/jpeg image/png application/pdf"
 * />
 *
 * // Single file with custom button text
 * <FileUpload
 *   files={files}
 *   onChange={setFiles}
 *   multiple={false}
 *   buttonText="Upload Document"
 *   acceptTypes="application/pdf"
 * />
 *
 * // With library selection
 * <FileUpload
 *   files={files}
 *   onChange={setFiles}
 *   showLibraryOption
 *   libraryFileTypes={['application/pdf']}
 * />
 * ```
 */

import { useState, useRef } from 'react';
import Compressor from 'compressorjs';
import {
	DocumentIcon,
	XMarkIcon,
	FolderOpenIcon,
} from '@heroicons/react/24/outline';
import { ArrowUpTrayIcon, PlusIcon } from '@heroicons/react/20/solid';

import useModal from '../../hooks/useModal';

const classNames = (...classes) => classes.filter(Boolean).join(' ');

/**
 * Format file size for display
 */
const formatFileSize = (bytes) => {
	if (!bytes) return '0 B';
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

/**
 * File preview item component
 */
const FilePreviewItem = ({ file, onRemove, index }) => {
	const isImage = file.type?.startsWith('image/') || file.thumbUrl;

	return (
		<div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
			<div className="flex items-center gap-3 min-w-0 flex-1">
				{isImage && file.thumbUrl ? (
					<img
						src={file.thumbUrl}
						alt={file.name}
						className="h-10 w-10 object-cover rounded flex-shrink-0"
					/>
				) : (
					<DocumentIcon className="h-10 w-10 text-gray-400 flex-shrink-0" />
				)}
				<div className="min-w-0 flex-1">
					<p className="text-sm font-medium text-gray-900 truncate">
						{file.name}
					</p>
					<p className="text-xs text-gray-500">
						{file.fromLibrary ? 'From Library' : formatFileSize(file.size)}
					</p>
				</div>
			</div>
			<button
				type="button"
				onClick={() => onRemove(index)}
				className="ml-2 p-1 text-gray-400 hover:text-red-500 flex-shrink-0"
			>
				<XMarkIcon className="h-5 w-5" />
			</button>
		</div>
	);
};

const FileUpload = ({
	files = [],
	onChange,
	multiple = true,
	acceptTypes = 'image/jpeg image/png application/pdf',
	maxSize = 10 * 1024 * 1024, // 10MB default
	showLibraryOption = false,
	libraryFileTypes = [],
	libraryMultiSelect = true,
	buttonText = 'Add Files',
	uploadText = 'Upload File',
	libraryText = 'Choose from Library',
	className = '',
	buttonClassName = '',
	disabled = false,
	compressImages = true,
	compressionOptions = {
		quality: 0.6,
		maxWidth: 1920,
		maxHeight: 1920,
		convertSize: 500000,
	},
}) => {
	const fileInputRef = useRef(null);
	const [showUploadOptions, setShowUploadOptions] = useState(false);
	const [isUploading, setIsUploading] = useState(false);

	const { setOpen: openDocumentPicker } = useModal('documentPicker');

	/**
	 * Handle file selection from input
	 */
	const handleFileChange = (e) => {
		const selectedFiles = Array.from(e.target.files || []);
		if (selectedFiles.length === 0) return;

		setIsUploading(true);

		// Process each file
		const processFile = (file) => {
			return new Promise((resolve) => {
				// Validate file size
				if (maxSize && file.size > maxSize) {
					alert(
						`File "${file.name}" exceeds maximum size of ${formatFileSize(maxSize)}`,
					);
					resolve(null);
					return;
				}

				// Compress images if enabled
				if (compressImages && file.type.startsWith('image/')) {
					new Compressor(file, {
						...compressionOptions,
						success: (compressedBlob) => {
							const compressedFile = new File([compressedBlob], file.name, {
								type: compressedBlob.type,
							});

							// Create thumbnail preview
							const reader = new FileReader();
							reader.onloadend = () => {
								resolve({
									name: file.name,
									original: compressedFile,
									size: compressedFile.size,
									type: compressedFile.type,
									thumbUrl: reader.result,
								});
							};
							reader.readAsDataURL(compressedBlob);
						},
						error: () => {
							// If compression fails, use original
							resolve({
								name: file.name,
								original: file,
								size: file.size,
								type: file.type,
							});
						},
					});
				} else {
					// Non-image files or compression disabled
					// Create a blob URL for PDF preview
					const previewUrl =
						file.type === 'application/pdf' ? URL.createObjectURL(file) : null;
					resolve({
						name: file.name,
						original: file,
						size: file.size,
						type: file.type,
						previewUrl,
					});
				}
			});
		};

		// Process all files
		Promise.all(selectedFiles.map(processFile)).then((processedFiles) => {
			const validFiles = processedFiles.filter(Boolean);

			if (multiple) {
				onChange([...files, ...validFiles]);
			} else {
				onChange(validFiles.slice(0, 1));
			}

			setIsUploading(false);
			setShowUploadOptions(false);

			// Reset input
			if (fileInputRef.current) {
				fileInputRef.current.value = '';
			}
		});
	};

	/**
	 * Handle library selection
	 */
	const handleSelectFromLibrary = () => {
		setShowUploadOptions(false);
		openDocumentPicker({
			multiSelect: libraryMultiSelect && multiple,
			fileTypes: libraryFileTypes.length > 0 ? libraryFileTypes : undefined,
			onSelect: (selectedDocs) => {
				if (!selectedDocs || selectedDocs.length === 0) return;

				const docsAsFiles = selectedDocs.map((doc) => ({
					name: doc.name || doc.fileName || 'document',
					size: doc.size || 0,
					type: doc.fileType || doc.contentType || 'application/octet-stream',
					id: doc.id,
					location: doc.location, // URL for library documents
					fromLibrary: true,
					original: null, // Mark as library file
				}));

				if (multiple) {
					onChange([...files, ...docsAsFiles]);
				} else {
					onChange(docsAsFiles.slice(0, 1));
				}
			},
		});
	};

	/**
	 * Remove file at index
	 */
	const handleRemoveFile = (index) => {
		onChange(files.filter((_, i) => i !== index));
	};

	/**
	 * Trigger file input click
	 */
	const handleUploadClick = () => {
		fileInputRef.current?.click();
		setShowUploadOptions(false);
	};

	return (
		<div className={className}>
			{/* Upload Button / Options */}
			{files.length === 0 || multiple ? (
				<div className="relative">
					<button
						type="button"
						onClick={() =>
							showLibraryOption
								? setShowUploadOptions(!showUploadOptions)
								: handleUploadClick()
						}
						disabled={disabled || isUploading}
						className={classNames(
							'w-full mt-3 flex items-center justify-center gap-2 px-4 py-3 text-xs font-medium rounded-lg transition-colors',
							disabled || isUploading
								? 'bg-gray-100 text-gray-400 cursor-not-allowed'
								: 'text-gray-600 border border-gray-200 hover:bg-gray-50',
							buttonClassName,
						)}
					>
						<PlusIcon className="w-4 h-4" />
						{isUploading ? 'Processing...' : buttonText}
					</button>

					{/* Upload Options Dropdown */}
					{showLibraryOption && showUploadOptions && !disabled && (
						<>
							<div
								className="fixed inset-0 z-40"
								onClick={() => setShowUploadOptions(false)}
							/>
							<div className="absolute right-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 w-56">
								<button
									type="button"
									onClick={handleUploadClick}
									className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-t-lg"
								>
									<ArrowUpTrayIcon className="w-4 h-4 text-gray-500" />
									{uploadText}
								</button>
								<button
									type="button"
									onClick={handleSelectFromLibrary}
									className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-b-lg border-t border-gray-100"
								>
									<FolderOpenIcon className="w-4 h-4 text-gray-500" />
									{libraryText}
								</button>
							</div>
						</>
					)}

					{/* Hidden file input */}
					<input
						ref={fileInputRef}
						type="file"
						accept={acceptTypes}
						multiple={multiple}
						onChange={handleFileChange}
						className="hidden"
						disabled={disabled}
					/>
				</div>
			) : null}

			{/* File Preview List */}
			{files.length > 0 && (
				<div className="space-y-2 mt-4">
					{files.map((file, index) => (
						<FilePreviewItem
							key={`file-${index}-${file.name}`}
							file={file}
							onRemove={handleRemoveFile}
							index={index}
						/>
					))}
				</div>
			)}

			{/* Upload spinner */}
			{isUploading && (
				<div className="flex items-center justify-center mt-3">
					<svg
						className="animate-spin h-5 w-5 text-indigo-600"
						xmlns="http://www.w3.org/2000/svg"
						fill="none"
						viewBox="0 0 24 24"
					>
						<circle
							className="opacity-25"
							cx="12"
							cy="12"
							r="10"
							stroke="currentColor"
							strokeWidth="4"
						/>
						<path
							className="opacity-75"
							fill="currentColor"
							d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
						/>
					</svg>
					<span className="ml-2 text-sm text-gray-500">Processing...</span>
				</div>
			)}
		</div>
	);
};

export default FileUpload;
